import { InputAdornment, TextField } from '@mui/material';
import type { InputBaseComponentProps } from '@mui/material/InputBase';
import { type ElementType } from 'react';
import { Controller, type FieldPath, useFormContext } from 'react-hook-form';
import type { PlansTabFormValues } from 'src/pages/Settings/memberships/components/plans-tab/types';
import NumericFormatInput from './numeric-format-input';

const PRICE_INTEGER_DIGITS_LIMIT = 6;
const PRICE_DECIMAL_DIGITS_LIMIT = 2;
const PRICE_PATTERN = new RegExp(
  `^\\d{1,${PRICE_INTEGER_DIGITS_LIMIT}}(\\.\\d{0,${PRICE_DECIMAL_DIGITS_LIMIT}})?$`,
);

type PlanPriceFieldProps = {
  name: FieldPath<PlansTabFormValues>;
  label: string;
  endAdornment: string;
  isRequired: boolean;
  disabled?: boolean;
};

export const PlanPriceField = ({
  name,
  label,
  endAdornment,
  isRequired,
  disabled = false,
}: PlanPriceFieldProps) => {
  const { control } = useFormContext<PlansTabFormValues>();

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: isRequired ? `${label} is required` : false,
        validate: (value) => {
          const stringValue = typeof value === 'string' ? value : '';

          if (!stringValue) {
            return isRequired ? `${label} is required` : true;
          }

          if (!PRICE_PATTERN.test(stringValue)) {
            return `Enter a valid ${label.toLowerCase()} with up to ${PRICE_DECIMAL_DIGITS_LIMIT} decimals`;
          }

          const numericValue = Number(stringValue);

          if (Number.isNaN(numericValue) || numericValue < 0) {
            return `${label} must be a non-negative amount`;
          }

          return true;
        },
      }}
      render={({ field, fieldState }) => (
        <TextField
          {...field}
          value={field.value ?? ''}
          onChange={(event) => {
            field.onChange(event);
          }}
          onBlur={field.onBlur}
          label={label}
          variant="outlined"
          size="small"
          fullWidth
          disabled={disabled}
          placeholder="0.00"
          error={!!fieldState.error}
          helperText={fieldState.error?.message}
          InputProps={{
            inputComponent: NumericFormatInput as unknown as ElementType<InputBaseComponentProps>,
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
            endAdornment: <InputAdornment position="end">{endAdornment}</InputAdornment>,
          }}
          inputProps={{
            name: field.name,
          }}
        />
      )}
    />
  );
};

export default PlanPriceField;
